import React, { Fragment, useState, useRef, useEffect, useContext } from 'react';
import Dropdown from 'react-dropdown';
import { useLazyQuery, gql } from '@apollo/client';
import QRCode from 'react-qr-code';

import MessageRotator from 'components/MessageRotator';
import { CONCIERGES, LOADING_MESSAGES, POPULAR_REGIONS, DEFAULT_DOCUMENT_TITLE, DEFAULT_SHARE_URL } from 'utils/Consts';
import { DebugContext } from 'utils/DebugContext';
import { isDev } from 'utils/Environment';
import { TRACK, recordEvent } from 'utils/Track';

import { ReactComponent as OOOLogoSvg } from 'assets/svg/ooo-logo.svg';
import { ReactComponent as ChevronDownSvg } from 'assets/svg/chevron-down.svg';
import { ReactComponent as LuggageSvg } from 'assets/svg/luggage.svg';

// Social Media Images
import instagramImage from 'assets/images/out-of-office-ooo-instagram.png';
import tiktokImage from 'assets/images/out-of-office-ooo-tiktok.png';
import facebookImage from 'assets/images/out-of-office-ooo-facebook.png';
import linkedinImage from 'assets/images/out-of-office-ooo-linkedin.png';

// Get App Images
import appleImage from 'assets/images/os-apple.png';
import androidImage from 'assets/images/os-android.png';

const SEARCH = gql`
  query($term: String!) {
    result: searchRegions(input: {
      term: $term
    }) {
      id
      name
      location
    }
  }
`;

const DAYS_OPTIONS = [ '1', '2', '3', '4', '5', '6', '7' ];
const CONCIERGE_OPTIONS = {
  ...CONCIERGES,
  surprise: { name: 'surprise', description: `I'm feelin' lucky` }
};

// Get region suggestions from env-specific region IDs (id: { dev, prod })
const getEnvRegionSuggestions = (regions) => {
  return regions.map(region => ({
    id: isDev ? region.id.dev : region.id.prod,
    name: region.name,
    location: region.location
  }));
};

// Cycle through actions in a decelerating fashion
const repeatWithDelay = (action, finalAction, endOnIndex, timeoutsRef) => {
  const minDelay = 150;
  const maxDelay = 700;
  const cycles = 2;
  // Cycle through all concierges N times, then land on the chosen concierge
  const numRepeats = (Object.keys(CONCIERGES)?.length * cycles) + (endOnIndex + 1);
  const curveFactor = 1.4;

  let accumulatedDelay = 0;

  for (let i = 0; i < numRepeats; i++) {
    const delay = Math.pow(curveFactor, i) * (maxDelay - minDelay) / (Math.pow(curveFactor, numRepeats) - 1) + minDelay;
    accumulatedDelay += delay;

    const timeoutId = setTimeout(() => {
      action();
      if (i === numRepeats - 1) {
        setTimeout(() => {
          finalAction();
        }, maxDelay * curveFactor);
      }
    }, accumulatedDelay);

    timeoutsRef.current.push(timeoutId);
  }
};

const DEFAULT_REGIONS = getEnvRegionSuggestions(POPULAR_REGIONS);

const ControlSection = ({
  onGenerateTrip,
  onShowSchedule,
  onHideSchedule,
  isTripLoading,
  isLoadingSimulated,
  isTripHasError,
  tripData,
  isFormReset,
  shareUrl = 'https://trips.takemeoutofoffice.com/',
}) => {
  const { isDebug } = useContext(DebugContext);

  const [destinationInputValue, setDestinationInputValue] = useState(''); // User input for destination
  const [selectedRegion, setSelectedRegion] = useState(null); // { id, name, location }
  const [isSuggestionsHidden, setIsSuggestionsHidden] = useState(true); // Whether to show suggestions dropdown
  const [suggestedRegions, setSuggestedRegions] = useState(DEFAULT_REGIONS); // List of suggested regions
  const [selectedNumOfDays, setSelectedNumOfDays] = useState('3'); // Number of days selected by user
  const [selectedVibe, setSelectedVibe] = useState(CONCIERGES.classic.name); // Vibe option selected by user (including surprise)
  const [chosenConcierge, setChosenConcierge] = useState(CONCIERGES.classic.name); // Concierge chosen by user or randomizer
  const [isChosenVibeHighlighted, setIsChosenVibeHighlighted] = useState(false); // Whether to highlight the chosen concierge
  const [isErrorMessageShowing, setIsErrorMessageShowing] = useState(false); // Whether to show error message (after MAX_MUTATION_ATTEMPTS)
  const [loadedImages, setLoadedImages] = useState([]); // List of images that have been loaded
  const eventContext = {
    section: TRACK.SECTIONS.CONTROL,
  };

  const [doGetSuggestedRegions, { loading: regionLoading, error: regionError, data: regionData }] = useLazyQuery(SEARCH);

  const isShowLoading = isTripLoading || isLoadingSimulated;
  const timeoutsRef = useRef([]);
  const destinationRef = useRef(null);

  // Stop cycling through concierges
  const clearConciergeRandomizerTimeouts = () => {
    timeoutsRef.current.forEach(timeoutId => clearTimeout(timeoutId));
    timeoutsRef.current = [];
  };

  // Cycle through concierges when user selects surprise and clicks generate
  const runConciegeRandomizer = () => {
    const pickedConcierge = Object.keys(CONCIERGES)[Math.floor(Math.random() * Object.keys(CONCIERGES).length)];
    setChosenConcierge(pickedConcierge);
    isDebug && console.log('Chosen Concierge:', pickedConcierge);
    let currentConciergeIndex = 0;
    setTimeout(() => {
      repeatWithDelay(
        // Action to repeat - cycle through concierges
        () => {
          setSelectedVibe(Object.keys(CONCIERGES)[currentConciergeIndex]);
          currentConciergeIndex = (currentConciergeIndex + 1) % Object.keys(CONCIERGES).length;
        },
        // Last action - show chosen concierge
        () => {
          setIsChosenVibeHighlighted(true);
          setSelectedVibe(pickedConcierge);
        },
        // Index of chosen concierge to end on
        Object.keys(CONCIERGES).indexOf(pickedConcierge),
        // Reference to timeouts array
        timeoutsRef
      );
    }, 150);
  };

  const handleDestinationInputChange = (e) => {
    const { value } = e.target;
    setDestinationInputValue(value);
    setSelectedRegion(null);
    if (value.length > 1) {
      setIsSuggestionsHidden(false);
      regionLoading && isDebug && console.log('Loading suggestions...');
      regionError && isDebug && console.log('Suggested regions error:', regionError);
      doGetSuggestedRegions({ variables: { term: value } });
    } else {
      setSuggestedRegions(DEFAULT_REGIONS);
    }
  };

  const handleDestinationInputFocus = (e) => {
    if (e?.target?.value?.length <= 1) {;
      setSuggestedRegions(DEFAULT_REGIONS);
    }
    setIsSuggestionsHidden(false);
  };

  const handleDestinationInputBlur = () => {
    if (suggestedRegions?.length > 0 && !isSuggestionsHidden && !selectedRegion) {
      const { name, location } = suggestedRegions[0];
      setSelectedRegion(suggestedRegions[0]);
      setDestinationInputValue(name + (location ? `, ${location}` : ''));
    }
    setIsSuggestionsHidden(true);
  };

  const handleDestinationClear = () => {
    setDestinationInputValue('');
    setSelectedRegion(null);
    setSuggestedRegions(DEFAULT_REGIONS);
    setIsSuggestionsHidden(false);
    destinationRef?.current?.focus();
  };

  const handleSelectRegion = (e, region) => {
    recordEvent(TRACK.EVENTS.SELECT_REGION, {
      ...eventContext,
      action: TRACK.ACTIONS.CLICK,
      destination: e?.target?.textContent,
      regionId: region?.id,
    });
    const { textContent } = e.target;
    setDestinationInputValue(textContent);
    setSelectedRegion(region);
    setIsSuggestionsHidden(true);
  };

  const handleSelectVibe = (event) => {
    recordEvent(TRACK.EVENTS.SELECT_VIBE, {
      ...eventContext,
      action: TRACK.ACTIONS.CLICK,
      selectedVibe: event.target.value,
    });
    setSelectedVibe(event.target.value);
  };

  const handlePressEnterOnDestinationInput = (e) => {
    e.preventDefault();
    destinationRef?.current?.blur();
  };

  const handleSubmitForm = (e) => {
    e.preventDefault();
    recordEvent(TRACK.EVENTS.GENERATE_TRIP, {
      ...eventContext,
      action: TRACK.ACTIONS.CLICK,
      destination: selectedRegion?.name,
      days: selectedNumOfDays,
      selectedVibe: selectedVibe,
    });
    isDebug && console.log('Selected Region:', selectedRegion);
    isDebug && console.log('Selected ID:', selectedRegion?.id);
    isDebug && console.log('Selected Num of Days:', selectedNumOfDays);
    if (selectedVibe === 'surprise') {
      runConciegeRandomizer();
    } else {
      isDebug && console.log('Chosen Concierge:', selectedVibe);
      setChosenConcierge(selectedVibe);
      setIsChosenVibeHighlighted(true);
    }
    isDebug && console.log('%c Generating trip!', 'background: #222; color: #1abc9c');
    onGenerateTrip({
      regionId: selectedRegion?.id,
      days: parseInt(selectedNumOfDays),
      travelerId: CONCIERGES?.[chosenConcierge]?.id?.[isDev ? 'dev' : 'prod']
    });
  };

  const handleDismissError = () => {
    recordEvent(TRACK.EVENTS.DISMISS_ERROR, {
      ...eventContext,
      action: TRACK.ACTIONS.CLICK,
    });
    setIsErrorMessageShowing(false);
  };

  const handleImageLoad = (url) => {
    setLoadedImages((prevLoadedImages) => [...prevLoadedImages, url]);
  };

  const handleOpenShareDialog = () => {
    recordEvent(TRACK.EVENTS.OPEN_MOBILE_SHARE_DIALOG, {
      ...eventContext,
      action: TRACK.ACTIONS.CLICK,
      currentShareUrl: shareUrl,
    });
    if (navigator.share) {
      isDebug && console.log('Share dialog opened');
      navigator.share({
        title: 'OOO. AI Trip Generator',
        text: 'Check out this app that automatically created my trip itinerary',
        url: DEFAULT_SHARE_URL
      }).then(() => {
        isDebug && console.log('Share option picked');
        recordEvent(TRACK.EVENTS.SELECT_MOBILE_SHARE_DIALOG_OPTION, {
          ...eventContext,
          action: TRACK.ACTIONS.CLICK,
          currentShareUrl: shareUrl,
        });
      })
      .catch(console.error);
    }
  };

  const renderShareLinks = ({ alwaysShow = false }) => {
    return (
      <div className={`share-links ${alwaysShow ? 'share-links-always-show' : ''}`}>
        Share:
        <a href='https://www.instagram.com/takemeoutofoffice' target='_blank'>
          <img src={instagramImage} alt='Share on Instagram' loading='lazy' />
        </a>
        <a href='https://www.tiktok.com/@takemeoutofoffice' target='_blank'>
          <img src={tiktokImage} alt='Share on TikTok' loading='lazy' />
        </a>
        <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(DEFAULT_SHARE_URL)}`} target='_blank'>
          <img src={facebookImage} alt='Share on Facebook' loading='lazy' />
        </a>
        <a href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(DEFAULT_SHARE_URL)}`} target='_blank'>
          <img src={linkedinImage} alt='Share on LinkedIn' loading='lazy' />
        </a>
      </div>
    );
  };

  useEffect(() => {
    if (!isTripLoading) {
      clearConciergeRandomizerTimeouts();
      setIsChosenVibeHighlighted(false);
    }
    if (!isShowLoading && tripData && !tripData?.errors && tripData?.trip?.itinerary?.length > 0) {
      isDebug && console.log('selectedVibe:', selectedVibe);
      isDebug && console.log('chosenConcierge:', chosenConcierge);
      onShowSchedule({ concierge: chosenConcierge, isNewSchedule: true });
    }
  }, [tripData, isTripLoading]);

  useEffect(() => {
    isDebug && console.log('isTripHasError:', isTripHasError);
    if (isTripHasError) {
      setIsChosenVibeHighlighted(false);
      setIsErrorMessageShowing(true);
      clearConciergeRandomizerTimeouts();
      document.title = DEFAULT_DOCUMENT_TITLE;
    }
  }, [isTripHasError]);

  useEffect(() => {
    if (isFormReset) {
      setIsSuggestionsHidden(true);
      setIsChosenVibeHighlighted(false);
    }
  }, [isFormReset]);

  useEffect(() => {
    if (regionData?.result?.length > 0) {
      setSuggestedRegions(regionData?.result);
    }
  }, [regionData?.result]);

  useEffect(() => {
    isDebug && console.log('QR Code URL:', shareUrl);
  }, [shareUrl]);

  return (
    <section id='content-left'>
      <div className='control-states-container'>
        <div className='control-header'>
          <a href='https://www.takemeoutofoffice.com/?ref=aitripgen' target='_blank'>
            <OOOLogoSvg width={153} title='Out Of Office App and Company Logo' />
          </a>
          <span className='subhead'>AI Trip Generator [BETA]</span>
        </div>
        <div className={`trip-create-container ${isShowLoading ? 'trip-create-loading' : ''}`}>
          <h2>Tell us about your trip!</h2>
          <form onSubmit={handlePressEnterOnDestinationInput}>
            <div className='trip-params'>
              <div className='destination'>
                <label className='field-name' htmlFor='destination'>Destination</label>
                <div className='destination-container'>
                  <input
                    type='text'
                    id='destination'
                    ref={destinationRef}
                    name='destination'
                    value={destinationInputValue}
                    className='Dropdown-control'
                    enterKeyHint={selectedRegion ? 'search' : 'enter'}
                    autoComplete='off'
                    placeholder='City'
                    onChange={handleDestinationInputChange}
                    onBlur={handleDestinationInputBlur}
                    onFocus={handleDestinationInputFocus}
                  />
                  <div
                    className={`x-button ${destinationInputValue === '' ? 'x-hidden' : ''}`}
                    onClick={handleDestinationClear}
                    onMouseDown={(e) => e.preventDefault()}
                  />
                </div>
                {suggestedRegions?.length > 0 && !isSuggestionsHidden
                  ? (
                    <div className='Dropdown-root'>
                      <div className='Dropdown-menu'>
                        {suggestedRegions?.map(region => (
                          <div
                            key={region?.id}
                            className='Dropdown-option'
                            // Makes onBlur run after selecting an option (vs onClick)
                            onMouseDown={e => e.preventDefault()}
                            onMouseUp={e => handleSelectRegion(e, region)}
                          >
                            {region?.name}{region?.location ? `, ${region?.location}` : ''}
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                  : null
                }
              </div>
              <div className='days'>
                <label className='field-name' htmlFor='days'>{/* How many days? / Days */}</label>
                <Dropdown
                  options={DAYS_OPTIONS}
                  value={selectedNumOfDays}
                  onChange={({ value }) => setSelectedNumOfDays(value > 7 ? 7 : value)}
                  arrowClosed={<ChevronDownSvg width={12} />}
                  arrowOpen={<ChevronDownSvg width={12} />}
                />
              </div>
            </div>
            <span className='field-name'>What's your trip vibe?</span>
            <div className={`vibe-container ${isChosenVibeHighlighted ? 'vibe-highlight-selected' : null}`}>
            {Object.values(CONCIERGE_OPTIONS)?.map(concierge => {
              const conciergeImage = require(`assets/images/${concierge?.name}.jpg`);
              return (
                <Fragment key={concierge?.name}>
                  <input type='radio' name='vibe' value={concierge?.name} id={concierge?.name} checked={selectedVibe === concierge?.name} onChange={handleSelectVibe} />
                  <label htmlFor={concierge?.name}>
                    <div className='vibe-image'>
                      <img
                        src={conciergeImage}
                        style={{opacity: loadedImages.includes(conciergeImage) ? 1 : 0}}
                        alt={`Go on a ${concierge?.name} trip - ${concierge?.description}`}
                        onLoad={() => handleImageLoad(conciergeImage)}
                        loading='lazy'
                      />
                      <div className='loading-spinner'></div>
                    </div>
                    <div className='vibe-info'>
                      <span className='vibe-name'>{concierge?.name}</span>
                      <span className='vibe-description'>{concierge?.description}</span>
                    </div>
                  </label>
                </Fragment>
              );
            })}
            
            </div>
            <button type='submit' onClick={handleSubmitForm} disabled={selectedRegion?.id === undefined}><span>Create my Itinerary</span></button>
          </form>
        </div>

        <div className={`loading-container ${isShowLoading ? 'loading-visible' : ''}`}>
          <div className='loading-spinner'></div>
          {isTripLoading && !isLoadingSimulated
            ? <MessageRotator messages={LOADING_MESSAGES} interval={2500} hidden={!isTripLoading || isLoadingSimulated} />
            : null
          }
          
        </div>

        <div className={`go-to-app-container ${isShowLoading ? 'trip-create-loading' : ''}`}>
          <h2>Want to save or edit this trip?</h2>
          <div className='qr-container'>
            <QRCode
              className='qr-code'
              value={shareUrl}
              size={105}
              fgColor='#194145'
              title='Your unique QR Code to save this trip in the Out of Office app'
            />
            <div className='app-info'>
              <h4>Scan to save this trip on the OOO app!</h4>
              <img src={appleImage} loading='lazy' alt='Apple Logo' />
              <img src={androidImage} loading='lazy' alt='Android Logo' />
            </div>
          </div>
        </div>
      </div>

      <div className={`error-container ${isErrorMessageShowing ? 'error-container-visible' : ''}`}>
        <div className={`error ${isErrorMessageShowing ? 'error-visible' : ''}`}>
          <LuggageSvg width={163} className='error-svg' />
          <span className='error-title'>Sorry, our systems are busy!</span>
          <span className='error-message'>Looks like a lot of people building trips at the moment. Please try again soon.</span>
          <button onClick={handleDismissError}>OK</button>
        </div>
      </div>

      <button className='create-another-trip' onClick={onHideSchedule}>{/* Create another trip */}</button>
      <button
        className={`back-to-trip ${tripData?.trip?.itinerary?.length > 0 && !isShowLoading ? '' : 'back-to-trip-hidden'}`}
        onClick={() => onShowSchedule({ concierge: chosenConcierge, isNewSchedule: false })}
      >
        {/* Back to trip */}
      </button>

      <div className='share-container'>
        {navigator.share ? <>
          <div className='mobile-share-link' onClick={handleOpenShareDialog}>Share</div>
          {renderShareLinks({ alwaysShow: false })}
        </> : <>
          {renderShareLinks({ alwaysShow: true })}
        </>}
      </div>
    </section>
  )
};

export default ControlSection;
